import { Box, CircularProgress, SxProps, Typography } from "@mui/material";
import { ChangeEvent, DragEvent, useRef, useState } from "react";
import { ReactComponent as AttachmentIcon } from "../../../../common/icons/attachment.svg";

interface Props {
  onFileSelected: (file: File) => void;
  accept?: string;
  disabled?: boolean;
  isUploading?: boolean;
  label: string;
  subLabel: string;
  uploadingLabel?: string;
  sx?: SxProps;
}

const FileDropzone = ({
  onFileSelected,
  accept,
  disabled = false,
  isUploading = false,
  label,
  subLabel,
  uploadingLabel,
  sx = {},
}: Props) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!disabled && !isUploading) setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled || isUploading) return;
    const file = e.dataTransfer.files[0];
    if (file) onFileSelected(file);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onFileSelected(file);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const isInteractive = !disabled && !isUploading;

  return (
    <Box
      component="label"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
        padding: "24px 16px",
        border: "2px dashed",
        borderColor: isDragging ? "primary.main" : "rgba(0, 0, 0, 0.2)",
        borderRadius: "8px",
        cursor: isInteractive ? "pointer" : "default",
        backgroundColor: isDragging
          ? "rgba(137, 95, 223, 0.05)"
          : "transparent",
        transition: "border-color 0.15s, background-color 0.15s",
        "&:hover": isInteractive ? { borderColor: "primary.main" } : undefined,
        ...sx,
      }}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        hidden
        disabled={disabled || isUploading}
        onChange={handleChange}
      />
      {isUploading ? (
        <CircularProgress size={22} />
      ) : (
        <AttachmentIcon style={{ width: 22, height: 22, opacity: 0.54 }} />
      )}
      <Typography
        variant="header"
        sx={{
          fontWeight: 700,
          fontSize: "14px",
          color: isUploading ? "text.disabled" : "text.primary",
        }}
      >
        {isUploading && uploadingLabel ? uploadingLabel : label}
      </Typography>
      {!isUploading && (
        <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
          {subLabel}
        </Typography>
      )}
    </Box>
  );
};

export default FileDropzone;
